import { createBrowserRouter, Navigate, type RouteObject } from "react-router";
import { ROUTES } from "@/shared/constants/routes";
import { AppShell } from "@/shared/layout/AppShell";
import { EmployeeOnlyGate } from "@/modules/auth/components/EmployeeOnlyGate";
import { OnboardingShell } from "@/shared/layout/OnboardingShell";
import { RequireSession, RequireSignedIn } from "./guards";
import { NotFoundPage } from "./NotFoundPage";
import { RouteErrorPage } from "./RouteErrorPage";
import { coworkingRoute } from "./coworkingRoutes";
import { faceKioskRoute } from "./faceKioskRoutes";
import { labourRoute } from "./labourRoutes";
import { payrollRoute } from "./payrollRoutes";
import { roomsRoute } from "./roomsRoutes";
import { staffRoute } from "./staffRoutes";
import { studentsRoute } from "./studentsRoutes";

/**
 * Lazy route table (rules/01 §1): importable without a router for tests. Pages are loaded on first
 * navigation; `handle.title` feeds the top bar. Three tiers: public, signed-in (onboarding), organisation (shell).
 */
export const routes: RouteObject[] = [
  {
    path: ROUTES.login,
    lazy: async () => ({ Component: (await import("@/modules/auth/pages/LoginPage")).LoginPage }),
    errorElement: <RouteErrorPage />,
  },
  {
    // Public on purpose: a person decides whether to sign up by reading these, and the sign-up
    // acceptance links to them (M6 §7). Draft wording; the pages say so.
    path: ROUTES.legalTerms,
    handle: { title: "Terms of Service" },
    lazy: async () => ({ Component: (await import("@/modules/legal/pages/TermsPage")).TermsPage }),
    errorElement: <RouteErrorPage />,
  },
  {
    path: ROUTES.legalPrivacy,
    handle: { title: "Privacy Notice" },
    lazy: async () => ({ Component: (await import("@/modules/legal/pages/PrivacyPage")).PrivacyPage }),
    errorElement: <RouteErrorPage />,
  },
  {
    path: ROUTES.accountDeletion,
    handle: { title: "Account deletion" },
    lazy: async () => ({
      Component: (await import("@/modules/auth/pages/AccountDeletionPage")).AccountDeletionPage,
    }),
    errorElement: <RouteErrorPage />,
  },
  {
    // Public on purpose, like the legal pages: the release history is linked from the store listings and
    // read by people who have not signed in. It fetches nothing (the list ships in the bundle).
    path: ROUTES.whatsNew,
    handle: { title: "What's new" },
    lazy: async () => ({
      Component: (await import("@/modules/releases/pages/WhatsNewPage")).WhatsNewPage,
    }),
    errorElement: <RouteErrorPage />,
  },
  {
    // Public on purpose: a visitor opens it from the SMS link on their own phone, with no account. It must answer
    // in place and never navigate away, because DLT refuses a whitelisted link that redirects.
    path: ROUTES.passLink,
    handle: { title: "Visitor pass" },
    lazy: async () => ({
      Component: (await import("@/modules/visitors/passLink/pages/PassLinkPage")).PassLinkPage,
    }),
    errorElement: <RouteErrorPage />,
  },
  {
    // Public on purpose, like the pass link: a wall screen has no account. It authenticates with its own paired
    // display token (never the portal session), so it sits outside the shell. M27 lane C4 builds the page.
    path: ROUTES.display,
    handle: { title: "Room display" },
    lazy: async () => ({
      Component: (await import("@/modules/rooms/publicDisplay/pages/RoomsPublicDisplayPage"))
        .RoomsPublicDisplayPage,
    }),
    errorElement: <RouteErrorPage />,
  },
  {
    // Public on purpose: the visual-regression fixture needs no session, so Playwright can screenshot it directly.
    path: ROUTES.kitchenSink,
    lazy: async () => ({
      Component: (await import("@/modules/kitchen-sink/pages/KitchenSinkPage")).KitchenSinkPage,
    }),
    errorElement: <RouteErrorPage />,
  },
  {
    path: "/onboarding",
    element: (
      <RequireSignedIn>
        <OnboardingShell />
      </RequireSignedIn>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to={ROUTES.onboardingCreateOrganisation} replace /> },
      {
        path: "create-organisation",
        handle: { title: "Create organisation" },
        lazy: async () => ({
          Component: (await import("@/modules/onboarding/pages/CreateOrganisationPage"))
            .CreateOrganisationPage,
        }),
      },
      {
        path: "invitations",
        handle: { title: "Invitations" },
        lazy: async () => ({
          Component: (await import("@/modules/onboarding/pages/InvitationsPage")).InvitationsPage,
        }),
      },
      { path: "*", handle: { title: "Not found" }, element: <NotFoundPage /> },
    ],
  },
  {
    path: "/",
    element: (
      <RequireSession>
        {/* M19 C1-15 (ADR-0023): an employee-only membership sees "use the mobile app", nothing else. */}
        <EmployeeOnlyGate>
          <AppShell />
        </EmployeeOnlyGate>
      </RequireSession>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <Navigate to={ROUTES.overview} replace /> },
      {
        path: "help",
        handle: { title: "Guides & workflows" },
        lazy: async () => ({ Component: (await import("@/modules/help/pages/HelpPage")).HelpPage }),
      },
      {
        path: "help/:slug",
        handle: { title: "Module guide" },
        lazy: async () => ({
          Component: (await import("@/modules/help/pages/HelpModulePage")).HelpModulePage,
        }),
      },
      {
        path: "overview",
        handle: { title: "Overview" },
        lazy: async () => ({
          Component: (await import("@/modules/overview/pages/OverviewPage")).OverviewPage,
        }),
      },
      {
        path: "events",
        handle: { title: "Events" },
        lazy: async () => ({ Component: (await import("@/modules/events/pages/EventsPage")).EventsPage }),
      },
      {
        path: "devices",
        handle: { title: "Devices" },
        lazy: async () => ({ Component: (await import("@/modules/devices/pages/DevicesPage")).DevicesPage }),
      },
      {
        path: "devices/:deviceId",
        handle: { title: "Device" },
        lazy: async () => ({
          Component: (await import("@/modules/devices/pages/DeviceDetailPage")).DeviceDetailPage,
        }),
      },
      {
        path: "people",
        handle: { title: "People" },
        lazy: async () => ({ Component: (await import("@/modules/people/pages/PeoplePage")).PeoplePage }),
      },
      {
        // M13 §3: the static segment wins over `people/:personId` whatever the order, but it reads
        // the way it resolves when it is written first.
        path: "people/groups",
        handle: { title: "Access groups" },
        lazy: async () => ({ Component: (await import("@/modules/people/pages/GroupsPage")).GroupsPage }),
      },
      {
        path: "people/groups/:groupId",
        handle: { title: "Access groups" },
        lazy: async () => ({
          Component: (await import("@/modules/people/pages/GroupDetailPage")).GroupDetailPage,
        }),
      },
      {
        path: "people/:personId",
        handle: { title: "Person" },
        lazy: async () => ({
          Component: (await import("@/modules/people/pages/PersonDetailPage")).PersonDetailPage,
        }),
      },
      {
        path: "doors",
        handle: { title: "Doors" },
        lazy: async () => ({ Component: (await import("@/modules/doors/pages/DoorsPage")).DoorsPage }),
      },
      // The M14 address of the ledger dues list, kept resolving rather than left a dead link.
      { path: "ledger", element: <Navigate to={ROUTES.gymLedger} replace /> },
      {
        // M16 wave 2 (ADR-0021 decision 9): the Visitors workspace. `visitors/visits` is declared
        // BEFORE `visitors/:visitorId`, or the register would match as a visitor whose id is the
        // word "visits" — the same ordering `people/groups` before `people/:personId` needs.
        path: "visitors/visits",
        handle: { title: "Visits" },
        lazy: async () => ({
          Component: (await import("@/modules/visitors/pages/VisitsPage")).VisitsPage,
        }),
      },
      {
        // Declared before `visitors/:visitorId` for the same reason `visitors/visits` is.
        path: "visitors/scans",
        handle: { title: "Live scans" },
        lazy: async () => ({
          Component: (await import("@/modules/visitors/pages/LiveScansPage")).LiveScansPage,
        }),
      },
      {
        // Visitors → Settings (visitor pass link). Declared before `visitors/:visitorId`, like the other static
        // segments, so it can never open as a visitor whose id is the word "settings".
        path: "visitors/settings",
        handle: { title: "Visitor settings" },
        lazy: async () => ({
          Component: (await import("@/modules/visitors/settings/pages/VisitorsSettingsPage"))
            .VisitorsSettingsPage,
        }),
      },
      {
        // The Visitors setup guide (setup-guides plan, lane C): `?step=<id>` opens one step and `?visit=<id>`
        // focuses the steps about one pass. Declared BEFORE `visitors/:visitorId`, for the same reason
        // `visitors/visits` and `visitors/scans` are: the router ranks a static segment above a parameter, but
        // written the way it resolves this can never become a visitor whose id is the word "setup". The
        // first-visit redirect that sends a desk here is the register's, never this route's.
        path: "visitors/setup",
        handle: { title: "Setup guide" },
        lazy: async () => ({
          Component: (await import("@/modules/visitors/setup/pages/VisitorsSetupPage")).VisitorsSetupPage,
        }),
      },
      {
        path: "visitors/:visitorId",
        handle: { title: "Visitor" },
        lazy: async () => ({
          Component: (await import("@/modules/visitors/pages/VisitorDetailPage")).VisitorDetailPage,
        }),
      },
      {
        path: "visitors",
        handle: { title: "Visitors" },
        lazy: async () => ({
          Component: (await import("@/modules/visitors/pages/VisitorsPage")).VisitorsPage,
        }),
      },
      {
        // The workspace root: the owner's dashboard (M15 §1 row 10.1), now that its api exists.
        path: "gym",
        handle: { title: "Gym dashboard" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymDashboardPage")).GymDashboardPage,
        }),
      },
      {
        // M17: the call list, and the same page with one enquiry's drawer open. TWO routes rather
        // than a query string because the drawer is a place — a bookmark, a back button and a
        // refresh all have to land on it — and one component because the list behind it must not be
        // thrown away to show it. `gym/leads` is declared first, so the static segment reads the way
        // it resolves.
        path: "gym/leads",
        handle: { title: "Leads" },
        lazy: async () => ({ Component: (await import("@/modules/gym/pages/GymLeadsPage")).GymLeadsPage }),
      },
      {
        path: "gym/leads/:leadId",
        handle: { title: "Leads" },
        lazy: async () => ({ Component: (await import("@/modules/gym/pages/GymLeadsPage")).GymLeadsPage }),
      },
      {
        path: "gym/plans",
        handle: { title: "Plans" },
        lazy: async () => ({ Component: (await import("@/modules/gym/pages/GymPlansPage")).GymPlansPage }),
      },
      {
        // The module's own page about ONE PERSON, keyed on the shared `people.id` — where the
        // membership, the plan and the ledger account live now that they are off the core person
        // page. Person-keyed and not member-keyed on purpose: a gym client who is also a gym trainer
        // is two relationships on one page about one human (the case the second addendum exists for).
        path: "gym/members/:personId",
        handle: { title: "Gym member" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymMemberPage")).GymMemberPage,
        }),
      },
      {
        path: "gym/members",
        handle: { title: "Gym members" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymMembersPage")).GymMembersPage,
        }),
      },
      {
        // TRN-001..003: the roster (add, remove), read by the register-member picker and by a
        // member's own "change trainer" control (`GymClientCard`) wherever a person's page is open.
        path: "gym/trainers",
        handle: { title: "Trainers" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymTrainersPage")).GymTrainersPage,
        }),
      },
      {
        // The dues list, moved here from the core nav's `/ledger` — a gated surface is a module
        // surface, whatever it shares its code with.
        path: "gym/ledger",
        handle: { title: "Ledger" },
        lazy: async () => ({ Component: (await import("@/modules/gym/pages/LedgerPage")).LedgerPage }),
      },
      {
        // The module's own reports hub: its own two reports (M15 §1 row 10.2) beside the five ledger
        // reports (M14 §4 wave 3b, ADR-0019 decision 9), moved off the core hub by the ADR-0017 addendum.
        path: "gym/reports",
        handle: { title: "Gym reports" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymReportsHubPage")).GymReportsHubPage,
        }),
      },
      {
        path: "gym/reports/memberships",
        handle: { title: "Memberships & expiry" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymMembershipsReportPage")).GymMembershipsReportPage,
        }),
      },
      {
        path: "gym/reports/dues",
        handle: { title: "Dues" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymDuesReportPage")).GymDuesReportPage,
        }),
      },
      {
        // M17 plan §6.4. In the MODULE's hub and never the core `/reports` one, which is the same
        // ADR-0017 addendum (2026-09-15) the two reports above obey.
        path: "gym/reports/leads",
        handle: { title: "Leads & conversions" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymLeadsReportPage")).GymLeadsReportPage,
        }),
      },
      {
        // M22 §6: the gym's four attendance reports, read from `attendance_daily` (a visit is one member
        // on one day, at any terminal). Each is its own lazy chunk, like the reports above.
        path: "gym/reports/visits",
        handle: { title: "Visits trend" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymVisitsReportPage")).GymVisitsReportPage,
        }),
      },
      {
        path: "gym/reports/attendance",
        handle: { title: "Member attendance" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymAttendanceReportPage")).GymAttendanceReportPage,
        }),
      },
      {
        path: "gym/reports/inactive",
        handle: { title: "Inactive members" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymInactiveReportPage")).GymInactiveReportPage,
        }),
      },
      {
        path: "gym/reports/peak-hours",
        handle: { title: "Peak hours" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymPeakHoursReportPage")).GymPeakHoursReportPage,
        }),
      },
      {
        path: "gym/reports/outstanding",
        handle: { title: "Outstanding balances" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/LedgerOutstandingReportPage"))
            .LedgerOutstandingReportPage,
        }),
      },
      {
        path: "gym/reports/payments",
        handle: { title: "Payments by day and method" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/LedgerPaymentsReportPage")).LedgerPaymentsReportPage,
        }),
      },
      {
        path: "gym/reports/statement",
        handle: { title: "Account statement" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/LedgerStatementReportPage"))
            .LedgerStatementReportPage,
        }),
      },
      {
        path: "gym/reports/receipts",
        handle: { title: "Receipts issued" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/LedgerReceiptsReportPage")).LedgerReceiptsReportPage,
        }),
      },
      {
        path: "gym/reports/corrections",
        handle: { title: "Reversals and adjustments" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/LedgerCorrectionsReportPage"))
            .LedgerCorrectionsReportPage,
        }),
      },
      {
        // GYM-002, GYM-003: grace, the dues flag (never a door), the entry group.
        path: "gym/settings",
        handle: { title: "Gym settings" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/pages/GymSettingsPage")).GymSettingsPage,
        }),
      },
      {
        // The Gym setup guide (setup-guides plan, lane B): `?step=<id>` opens one step and `?person=<id>` focuses
        // the steps about one member. The title is the breadcrumb's ("Gym › Setup guide") and the sidebar entry's
        // name; the page's own heading is "Set up the Gym". The first-visit redirect that sends an owner here is
        // the Dashboard's, never this route's.
        path: "gym/setup",
        handle: { title: "Setup guide" },
        lazy: async () => ({
          Component: (await import("@/modules/gym/setup/pages/GymSetupPage")).GymSetupPage,
        }),
      },
      // M19 (contracts §11): the Staff workspace, under one lazy, gated layout — see `staffRoutes.tsx`.
      staffRoute,
      // M20 (contracts §11): the Student Attendance workspace — see `studentsRoutes.tsx`.
      studentsRoute,
      // M25 (contracts §15): the Labour workspace — see `labourRoutes.tsx`.
      labourRoute,
      // M21 (contracts §15): the Co-working workspace — see `coworkingRoutes.tsx`.
      coworkingRoute,
      payrollRoute,
      // M27 (contracts §18): the Rooms and Equipment workspace — see `roomsRoutes.tsx`.
      roomsRoute,
      // M24 (contracts §11): the Face Kiosk workspace — see `faceKioskRoutes.tsx`.
      faceKioskRoute,
      // The M14 addresses of the five ledger reports; each redirects to its new address under the Gym
      // module's own reports hub (ADR-0017 addendum 2026-09-15) rather than leaving a dead link.
      { path: "reports/ledger/outstanding", element: <Navigate to={ROUTES.gymReportsOutstanding} replace /> },
      { path: "reports/ledger/payments", element: <Navigate to={ROUTES.gymReportsPayments} replace /> },
      { path: "reports/ledger/statement", element: <Navigate to={ROUTES.gymReportsStatement} replace /> },
      { path: "reports/ledger/receipts", element: <Navigate to={ROUTES.gymReportsReceipts} replace /> },
      { path: "reports/ledger/corrections", element: <Navigate to={ROUTES.gymReportsCorrections} replace /> },
      /**
       * The core Reports hub is gone (redesign 2026-09, pass 07). Every card on it needed only the
       * `reports:view` the nav entry already gates, so there was nothing to filter per card — a page
       * whose only logic is the logic of the link that reached it is a menu. Its one real piece of
       * content, the list of six questions, is now a flyout on every report's own header.
       *
       * `/reports` stays the nav entry's address so every `/reports/*` page lights it, and lands on
       * Attendance. The Gym module keeps its own hub: retiring that one would push seven report
       * entries into the Gym sidebar.
       */
      { path: "reports", element: <Navigate to={ROUTES.reportsAttendance} replace /> },
      {
        /**
         * Still its own page. Pass 07 folds it into the Attendance grid's last columns — the same
         * endpoint feeding them, so nothing is lost and the two screens stop having to agree about
         * the word "absent" — but that fold is not built yet, and redirecting this address before
         * the columns exist would quietly take a report customers use away from them.
         */
        path: "reports/summary",
        handle: { title: "Attendance summary" },
        lazy: async () => ({
          Component: (await import("@/modules/reports/pages/AttendanceSummaryPage")).AttendanceSummaryPage,
        }),
      },
      {
        path: "reports/attendance",
        handle: { title: "Attendance" },
        lazy: async () => ({
          Component: (await import("@/modules/reports/pages/AttendancePage")).AttendancePage,
        }),
      },
      {
        path: "reports/enrolment",
        handle: { title: "Enrolment status" },
        lazy: async () => ({
          Component: (await import("@/modules/reports/pages/EnrolmentPage")).EnrolmentPage,
        }),
      },
      {
        path: "reports/muster",
        handle: { title: "Daily muster" },
        lazy: async () => ({
          Component: (await import("@/modules/reports/pages/MusterPage")).MusterPage,
        }),
      },
      {
        path: "reports/terminals",
        handle: { title: "Terminal activity" },
        lazy: async () => ({
          Component: (await import("@/modules/reports/pages/TerminalActivityPage")).TerminalActivityPage,
        }),
      },
      {
        path: "reports/denied",
        handle: { title: "Denied access" },
        lazy: async () => ({
          Component: (await import("@/modules/reports/pages/DeniedAccessPage")).DeniedAccessPage,
        }),
      },
      {
        // M9 §5: templates and holiday lists, two tabs behind `?tab=`.
        path: "schedules",
        handle: { title: "Schedules" },
        lazy: async () => ({
          Component: (await import("@/modules/schedules/pages/SchedulesPage")).SchedulesPage,
        }),
      },
      // The M1 address of Members & roles. The redesign folded both surfaces into Settings tabs
      // (pass 01 board 1f), and this address is in invitation emails and runbooks, so it resolves.
      { path: "members", element: <Navigate to={ROUTES.members} replace /> },
      {
        path: "settings/subscription/change",
        handle: { title: "Change plan" },
        lazy: async () => ({
          Component: (await import("@/modules/settings/pages/ChangePlanPage")).ChangePlanPage,
        }),
      },
      {
        path: "settings",
        handle: { title: "Settings" },
        lazy: async () => ({
          Component: (await import("@/modules/settings/pages/SettingsPage")).SettingsPage,
        }),
      },
      // The M1 address of the organisation form; old links land on the same tab.
      { path: "settings/organisation", element: <Navigate to={ROUTES.settings} replace /> },
      {
        // The Access Control setup guide (setup-guides plan, lane A): `?step=<id>` opens one step and `?person=<id>`
        // focuses the steps about one person. A CORE address — no workspace prefix claims `/setup` — so the
        // breadcrumb reads "Access control › Setup guide" and the sidebar's ninth entry lights it. Written before
        // the `*` catch-all, which would answer 404 for it. The first-visit redirect that sends a new owner here
        // is Overview's, never this route's. The page lives in `settings/setup/` (the organisation-level module
        // owns the forms it embeds), the heading is "Set up Access Control".
        path: "setup",
        handle: { title: "Setup guide" },
        lazy: async () => ({
          Component: (await import("@/modules/settings/setup/pages/CoreSetupPage")).CoreSetupPage,
        }),
      },
      { path: "*", handle: { title: "Not found" }, element: <NotFoundPage /> },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
